/* eslint-disable max-lines */
import {
  ChooseType,
  HG_RETS_CATEGORIES_BY_CHOOSETYPE,
  HgSupportedLanguage,
  OfferType,
  SearchModel,
} from '@smg-real-estate/hg-search-libs';

import { createSRPRoute as appCreateSRPRoute } from '@/resultlist/srp-uri';
import { HgBreadcrumbModel, SupportedPlatforms } from '@smg-real-estate/domus';
import { GeoLocation } from '@smg-real-estate/geo-libs';
import { OfferType as HgRetsOfferType } from '@smg-real-estate/hg-rets-schema';

import { useGeoStore } from '@/store/modules/geo';
import { HG_PROPERTY_SEGMENTS } from './hg-property-types';
import { IS24_PROPERTY_SEGMENTS } from './is24-property-types';

export const SORTED_GEO_LEVELS = [
  'country',
  'canton',
  'region',
  'city',
  'cityregion',
  'citydistrict',
  'zipcode',
];

function matchesWithCategories(chooseType: ChooseType, categories: string[]): boolean {
  return HG_RETS_CATEGORIES_BY_CHOOSETYPE[chooseType].some((category) =>
    categories.includes(category),
  );
}

export const findMatchingChooseType = (offerType: OfferType, categories: string[]): ChooseType => {
  if (offerType === OfferType.RENT) {
    return (
      [
        ChooseType.rentgarage,
        ChooseType.rentstorage,
        ChooseType.rentoffice,
        ChooseType.rentbobj,
        ChooseType.rentfurnflat,
        ChooseType.rentflatonly,
        ChooseType.rentliving,
        ChooseType.rentflat,
      ].find((ct) => matchesWithCategories(ct, categories)) ?? ChooseType.rentall
    );
  } else {
    return (
      [
        ChooseType.purchland,
        ChooseType.purchgarage,
        ChooseType.purchbobj,
        ChooseType.purchofhbobj,
        ChooseType.purchbof,
        ChooseType.purchofh,
        ChooseType.purchflat,
        ChooseType.purchfaofh,
      ].find((ct) => matchesWithCategories(ct, categories)) ?? ChooseType.purchall
    );
  }
};

export const geoTagSortFunction = (tagA: string, tagB: string): number => {
  // extract the 'city' part from geo-city-zurich, etc:
  const geoLevelA = (tagA.match(/([a-z]+)/g) as RegExpMatchArray)[1];
  const geoLevelB = (tagB.match(/([a-z]+)/g) as RegExpMatchArray)[1];
  return SORTED_GEO_LEVELS.indexOf(geoLevelA) - SORTED_GEO_LEVELS.indexOf(geoLevelB);
};

export const getOfferTypeLink = (offerType: OfferType, language: HgSupportedLanguage): string => {
  const baseUrl = import.meta.env.VUE_APP_BASE_URL as string;

  // Define path patterns for each platform, language and offer type
  const paths = {
    homegate: {
      [OfferType.BUY]: {
        [HgSupportedLanguage.DE]: '/kaufen/immobilie-suchen',
        [HgSupportedLanguage.FR]: '/acheter/recherche-immobiliere',
        [HgSupportedLanguage.EN]: '/buy/real-estate-search',
        [HgSupportedLanguage.IT]: '/acquistare/ricerca-immobiliare',
      },
      [OfferType.RENT]: {
        [HgSupportedLanguage.DE]: '/mieten/immobilie-suchen',
        [HgSupportedLanguage.FR]: '/louer/recherche-immobiliere',
        [HgSupportedLanguage.EN]: '/rent/real-estate-search',
        [HgSupportedLanguage.IT]: '/affittare/ricerca-immobiliare',
      },
    },
    immoscout24: {
      [OfferType.BUY]: {
        [HgSupportedLanguage.DE]: '/kaufen/immobilien',
        [HgSupportedLanguage.FR]: '/acheter/immobilier',
        [HgSupportedLanguage.EN]: '/buy/real-estate',
        [HgSupportedLanguage.IT]: '/acquistare/immobiliare',
      },
      [OfferType.RENT]: {
        [HgSupportedLanguage.DE]: '/mieten/immobilien',
        [HgSupportedLanguage.FR]: '/louer/immobilier',
        [HgSupportedLanguage.EN]: '/rent/real-estate',
        [HgSupportedLanguage.IT]: '/affittare/immobiliare',
      },
    },
  };

  // Determine which platform we're on based on the base URL
  const platform = baseUrl.includes('immoscout24') ? 'immoscout24' : 'homegate';

  // Construct the full URL
  return `${baseUrl}${paths[platform][offerType][language]}`;
};

export const bcTrackHome: () => string = () => 'home';

export const bcTrackEntryPage: (OfferType: OfferType) => string = (offerType: OfferType) =>
  `entryPage${offerType}`;

export const bcTrackSeoEntryPage: (name: string) => string = (name: string) =>
  `seoEntryPage_${name}`;

export const bcTrackDrillDown: (OfferType: OfferType) => string = (offerType: OfferType) =>
  `drillDownPage${offerType}`;

export const bcTrackDrillDownLocation: (
  offerType: OfferType,
  geoLocation: GeoLocation | undefined,
) => string = (offerType: OfferType, geoLocation?: GeoLocation | undefined) =>
  geoLocation !== undefined ? `${bcTrackDrillDown(offerType)}_${geoLocation.type}` : '';

/**
 * Mapping of property types to URL segments for each language
 * This approach allows for easy extension and maintenance
 */
const PROPERTY_TYPE_URL_MAP: Record<string, keyof typeof HG_PROPERTY_SEGMENTS> = {
  // Buy property types
  [ChooseType.purchofh]: 'HOUSE',
  [ChooseType.purchflat]: 'FLAT',
  [ChooseType.purchfaofh]: 'HOUSE',
  [ChooseType.purchbobj]: 'COMMERCIAL',
  [ChooseType.purchland]: 'PLOT',
  [ChooseType.purchgarage]: 'PARKING',
  [ChooseType.purchbof]: 'MULTI_FAMILY_HOUSE',
  [ChooseType.purchofhbobj]: 'INVESTMENT',
  [ChooseType.purchall]: 'REAL_ESTATE',

  // Rent property types
  [ChooseType.rentofh]: 'HOUSE',
  [ChooseType.rentflat]: 'FLAT',
  [ChooseType.rentflatonly]: 'FLAT',
  [ChooseType.rentfurnflat]: 'FURNISHED_FLAT',
  [ChooseType.rentbobj]: 'COMMERCIAL',
  [ChooseType.rentstorage]: 'COMMERCIAL',
  [ChooseType.rentoffice]: 'COMMERCIAL',
  [ChooseType.rentgarage]: 'PARKING',
  [ChooseType.rentall]: 'REAL_ESTATE',
  [ChooseType.rentliving]: 'REAL_ESTATE',
};

/**
 * Gets property type segment for a given platform and language
 */
const getPropertyTypeSegment = (
  propertyTypeKey: keyof typeof HG_PROPERTY_SEGMENTS,
  language: HgSupportedLanguage,
  platform?: SupportedPlatforms,
): string => {
  if (platform === SupportedPlatforms.is24) {
    const is24Segment = IS24_PROPERTY_SEGMENTS[propertyTypeKey]?.[language];
    if (is24Segment) return is24Segment;
  }
  return (
    HG_PROPERTY_SEGMENTS[propertyTypeKey][language] ||
    HG_PROPERTY_SEGMENTS[propertyTypeKey][HgSupportedLanguage.EN]
  );
};

/**
 * Returns the localized offer type path segment for URLs
 * Note: URL path segments are not translated through i18n files, but rather
 * use predefined URL patterns for different languages for SEO purposes.
 *
 * @param offerType - The offer type (BUY or RENT)
 * @param language - The current language
 */
export const getLocalizedOfferTypePath = (
  offerType: OfferType,
  language: HgSupportedLanguage,
): string => {
  // Define path segments for each language and offer type
  const offerTypePaths = {
    [OfferType.RENT]: {
      [HgSupportedLanguage.DE]: '/mieten',
      [HgSupportedLanguage.FR]: '/louer',
      [HgSupportedLanguage.IT]: '/affittare',
      [HgSupportedLanguage.EN]: '/rent',
    },
    [OfferType.BUY]: {
      [HgSupportedLanguage.DE]: '/kaufen',
      [HgSupportedLanguage.FR]: '/acheter',
      [HgSupportedLanguage.IT]: '/acquistare',
      [HgSupportedLanguage.EN]: '/buy',
    },
  };

  // Return path segment with fallback
  return offerTypePaths[offerType]?.[language] || (offerType === OfferType.RENT ? '/rent' : '/buy');
};

/**
 * Returns the generic property type path for the given language.
 * This should be used for location links to ensure they use the correct path.
 * Note: URL path segments are not translated through i18n files, but rather
 * use predefined URL patterns for different languages for SEO purposes.
 *
 * @param language - The current language
 * @param platform - The platform (for IS24-specific handling)
 */
export const getGenericPropertyTypePath = (
  language: HgSupportedLanguage,
  platform?: SupportedPlatforms,
): string => {
  return getPropertyTypeSegment('REAL_ESTATE', language, platform);
};

/**
 * Returns the localized property type path for a specific choose type.
 * For location links, use getGenericPropertyTypePath() instead.
 * Note: URL path segments are not translated through i18n files, but rather
 * use predefined URL patterns for different languages for SEO purposes.
 *
 * @param chooseType - The choose type to convert to a property type path
 * @param language - The current language
 * @param platform - The platform (for IS24-specific handling)
 */
export const getPropertyTypeFromChooseType = (
  chooseType: ChooseType | undefined,
  language: HgSupportedLanguage,
  platform?: SupportedPlatforms,
): string => {
  if (!chooseType) {
    return getGenericPropertyTypePath(language, platform);
  }

  // Get the property type key for the choose type
  const propertyTypeKey = PROPERTY_TYPE_URL_MAP[chooseType];

  // If we have a mapping for this choose type, use it
  if (propertyTypeKey) {
    return getPropertyTypeSegment(propertyTypeKey, language, platform);
  }

  // Fallback to generic property type
  return getGenericPropertyTypePath(language, platform);
};

/**
 * Convert HgRetsOfferType to OfferType
 *
 * @param hgRetsOfferType - The HgRetsOfferType to convert
 */
export const convertToOfferType = (hgRetsOfferType: HgRetsOfferType): OfferType => {
  return hgRetsOfferType === HgRetsOfferType.Rent ? OfferType.RENT : OfferType.BUY;
};

/**
 * Creates the home page breadcrumb link
 *
 * @param language - The current language
 * @param baseUrl - The base URL for the platform
 * @param i18n - The i18n function to translate text
 * @param platform - The platform (is24 or homegate)
 */
export const createHomePageLink = (
  language: HgSupportedLanguage,
  baseUrl: string,
  i18n: (key: string) => string,
  platform?: SupportedPlatforms,
): HgBreadcrumbModel => {
  // IS24 and Homegate have the same homepage structure: domain/language
  return {
    name: i18n('breadCrumbs.homepage'),
    link: `${baseUrl}/${language}`,
    trackingMeta: bcTrackHome(),
  };
};

/**
 * Creates the offer type breadcrumb link for Homegate
 *
 * @param offerType - The offer type
 * @param language - The current language
 * @param i18n - The i18n function to translate text
 */
export const createOfferTypeLink = (
  offerType: OfferType,
  language: HgSupportedLanguage,
  i18n: (key: string) => string,
): HgBreadcrumbModel => {
  return {
    name: i18n(`breadCrumbs.offerType.${offerType}`),
    link: getOfferTypeLink(offerType, language),
    trackingMeta: bcTrackEntryPage(offerType),
  };
};

/**
 * Creates a choose type breadcrumb link based on the current platform
 *
 * @param offerType - The offer type
 * @param chooseType - The choose type
 * @param language - The current language
 * @param baseUrl - The base URL for the platform
 * @param i18n - The i18n function to translate text
 * @param platform - The platform (is24 or homegate)
 * @param routePath - The path from the route
 */
export const createChooseTypeLink = (
  offerType: OfferType,
  chooseType: ChooseType,
  language: HgSupportedLanguage,
  baseUrl: string,
  i18n: (key: string) => string,
  platform: SupportedPlatforms,
  routePath: string,
): HgBreadcrumbModel => {
  const propertyType = getPropertyTypeFromChooseType(chooseType, language, platform);
  const offerTypePath = getLocalizedOfferTypePath(offerType, language);

  // URL structure differs between platforms
  let fullPath;
  if (platform === SupportedPlatforms.is24) {
    // For IS24: domain/language/property-type/offer-type
    // Remove the leading slash from offerTypePath since we're building the full path
    const localizedOfferType = offerTypePath.substring(1); // Remove leading '/'

    // For all property types in IS24, use the localized structure
    fullPath = `/${language}/${propertyType}/${localizedOfferType}`;
  } else {
    // For Homegate: domain/offer-type/property-type/location
    fullPath = `${offerTypePath}/${propertyType}${routePath}`;
  }

  // The translation key differs between platforms
  const translationPrefix =
    platform === SupportedPlatforms.is24 ? 'resultlist.breadcrumbs.' : 'resultlist.chooseType.';

  return {
    name: i18n(`${translationPrefix}${chooseType}`),
    link: `${baseUrl}${fullPath}`,
    trackingMeta: bcTrackDrillDown(offerType),
  };
};

/**
 * Extracts the location path from a route
 */
function extractLocationPath(route: any): string {
  if (!route || !route.path) {
    return '';
  }

  const pathParts = route.path.split('/').filter((part: string) => part);
  if (pathParts.length === 0) {
    return '';
  }

  const lastPart = pathParts[pathParts.length - 1];
  return lastPart && !lastPart.match(/^(en|de|fr|it)$/) ? `/${lastPart}` : '';
}

/* eslint-disable max-statements */
export const createLocationLinks = (
  geoTags: string[],
  offerType: OfferType,
  language: HgSupportedLanguage,
  baseUrl: string,
  platform: SupportedPlatforms,
  getGeoLocationById: (id: string) => GeoLocation | undefined,
  getGeoLocationName: (id: string, language: HgSupportedLanguage) => string | undefined,
  routeObject: any,
  geoStore: ReturnType<typeof useGeoStore>,
): HgBreadcrumbModel[] => {
  const isIs24 = platform === SupportedPlatforms.is24;

  const validLinks: HgBreadcrumbModel[] = [];

  geoTags
    .filter((tag) => {
      return isIs24 ? !tag.startsWith('geo-country') : tag;
    })
    .filter((tag) => !tag.startsWith('geo-zipcode'))
    .sort(geoTagSortFunction)
    .forEach((tag) => {
      const searchModel = new SearchModel().withOfferType(offerType).withLocations([tag]);
      try {
        const route = appCreateSRPRoute(language, searchModel, routeObject, geoStore);
        const geoLocation: GeoLocation | undefined = getGeoLocationById(tag);
        const locationName = getGeoLocationName(tag, language);

        // Skip if the location name is undefined
        if (locationName === undefined) {
          return;
        }

        // For location links, we should always use the generic property type (real-estate or localized equivalent)
        const genericPropertyType = getGenericPropertyTypePath(language, platform);

        // URL structure differs between platforms
        let fullPath;
        if (isIs24) {
          // For IS24: domain/language/property-type/offer-type/location
          const offerTypePath = getLocalizedOfferTypePath(offerType, language);
          const localizedOfferType = offerTypePath.substring(1); // Remove leading '/'

          // Extract location part and build path
          const locationPath = extractLocationPath(route);
          fullPath = `/${language}/${genericPropertyType}/${localizedOfferType}${locationPath}`;
        } else {
          // For Homegate: domain/offer-type/property-type/location
          const offerTypePath = getLocalizedOfferTypePath(offerType, language);
          fullPath = `${offerTypePath}/${genericPropertyType}${route.path}`;
        }

        validLinks.push({
          name: locationName,
          link: `${baseUrl}${fullPath}`,
          trackingMeta: bcTrackDrillDownLocation(offerType, geoLocation),
        });
      } catch (error) {
        console.warn('Error creating location link:', error);
      }
    });

  return validLinks;
};
/* eslint-enable max-statements */

/**
 * Creates breadcrumbs for the detail page
 *
 * @param platform - The current platform (is24 or homegate)
 * @param listing - The listing object
 * @param language - The current language
 * @param baseUrl - The base URL for the platform
 * @param i18n - The i18n function to translate text
 * @param getGeoLocationById - Function to get geo location by ID
 * @param getGeoLocationName - Function to get geo location name
 * @param routeObject - Route object for creating SRP route
 * @param storeObject - Store object for creating SRP route
 */
export const createDetailPageBreadcrumbs = (
  platform: SupportedPlatforms,
  listing: any,
  language: HgSupportedLanguage,
  baseUrl: string,
  i18n: (key: string) => string,
  getGeoLocationById: (id: string) => GeoLocation | undefined,
  getGeoLocationName: (id: string, language: HgSupportedLanguage) => string | undefined,
  routeObject: any,
  geoStore: ReturnType<typeof useGeoStore>,
): HgBreadcrumbModel[] => {
  if (!listing) {
    return [];
  }

  const offerType = convertToOfferType(listing.offerType);
  const isIs24 = platform === SupportedPlatforms.is24;

  const homePageLink = createHomePageLink(language, baseUrl, i18n, platform);
  const breadcrumbs: HgBreadcrumbModel[] = [homePageLink];

  // IS24 doesn't have offer type link (skips directly to choose type)
  if (!isIs24) {
    const offerTypeLink = createOfferTypeLink(offerType, language, i18n);
    breadcrumbs.push(offerTypeLink);
  }

  // Add choose type link
  const chooseType = findMatchingChooseType(offerType, listing.categories || []);
  const chooseTypeSearchModel = new SearchModel().withChooseType(chooseType);

  let chooseTypeRoute = { path: '' };
  try {
    chooseTypeRoute = appCreateSRPRoute(language, chooseTypeSearchModel, routeObject, geoStore);
  } catch (error) {
    console.warn('Error creating choose type route:', error);
  }

  const chooseTypeLink = createChooseTypeLink(
    offerType,
    chooseType,
    language,
    baseUrl,
    i18n,
    platform,
    chooseTypeRoute.path,
  );
  breadcrumbs.push(chooseTypeLink);

  // Add location links
  const geoTags = listing.address?.geoTags || [];
  const locationLinks = createLocationLinks(
    geoTags,
    offerType,
    language,
    baseUrl,
    platform,
    getGeoLocationById,
    getGeoLocationName,
    routeObject,
    geoStore,
  );

  return [...breadcrumbs, ...locationLinks];
};

/**
 * Helper function for importing in other files where SRP routes are needed
 * This now forwards to the real implementation
 */
export function createSRPRoute(
  language: HgSupportedLanguage,
  searchModel: SearchModel,
  routeObject: any,
  geoStore: ReturnType<typeof useGeoStore>,
): { path: string } {
  try {
    return appCreateSRPRoute(language, searchModel, routeObject, geoStore);
  } catch (error) {
    console.warn('Error in createSRPRoute:', error);
    return { path: '' };
  }
}
